import React from "react";
import { Icon_Tick } from "@assets/images";
import { IEvidence, LearningObjective } from "@interfaces/Interfaces";
import classNames from "classnames";
import {
  getManualOccurrencesOfLearningOutcome,
  getAutomaticOccurrencesOfLearningOutcome,
  getOccurrencesOfLearningOutcome,
  getLearningOutcomesAchieved,
  getLearningOutcomesPartiallyMet,
  learningOutcomesHavePartialState,
} from "@utils/nqpUtils";
import { IonItem } from "@ionic/react";
import * as SPAPRPUtils from "@utils/programs/SPAPRPUtils";

type Props = {
  evidence: IEvidence[];
  learningOutcomes: {
    [x: string]: Partial<Record<string, LearningObjective[]>>;
  }[];
  useAutoLOs: boolean;
  hideTotal?: boolean;
  rowsAsButtons?: boolean;
  learningOutcomeRowPressed: (learningOutcome: LearningObjective) => void;
  useRAGForTotal?: boolean;
  sortLOsUsingDisplayName?: boolean;
};

const getTextColor = (count: number) => {
  if (count > 0) {
    return "!text-orange-50";
  }

  return "!text-cta-red";
};

const LearningOutcomesList: React.FC<Props> = (props) => {
  return (
    <div className="relative bg-white">
      {props.useAutoLOs && (
        <div className="sticky flex flex-row justify-end px-4">
          <div className="flex w-11 justify-center">
            <div className="text-acc-15px font-acc-700 tracking-default text-grey-50">{"Auto"}</div>
          </div>
          <div className="ml-2 flex w-11 justify-center">
            <div className="text-acc-15px font-acc-700 tracking-default text-grey-50">{"Manual"}</div>
          </div>
        </div>
      )}
      {props.learningOutcomes.map((item) => {
        const topSectionKey = Object.keys(item)[0];
        const topSection = item[topSectionKey];
        const object = Object.keys(topSection).map((key) => topSection[key]);
        const sections: LearningObjective[][] = object.filter(
          (section) => section !== undefined
        ) as LearningObjective[][];

        const learnObjectivesForTopSection = sections.flat();
        const totalAchievedForSection = getLearningOutcomesAchieved(
          learnObjectivesForTopSection,
          props.evidence,
          props.useAutoLOs
        );

        const percentageForSection =
          learnObjectivesForTopSection.length > 0
            ? Math.trunc((totalAchievedForSection / learnObjectivesForTopSection.length) * 100)
            : 0;

        const sectionHasPartialState = learningOutcomesHavePartialState(learnObjectivesForTopSection);
        const totalPartialForSection = sectionHasPartialState
          ? getLearningOutcomesPartiallyMet(learnObjectivesForTopSection, props.evidence, props.useAutoLOs)
          : undefined;
        const partialPercentageForSection =
          totalPartialForSection !== undefined && learnObjectivesForTopSection.length > 0
            ? Math.trunc((totalPartialForSection / learnObjectivesForTopSection.length) * 100)
            : undefined;

        return (
          <div key={topSectionKey}>
            <div className="bg-white px-4 py-2">
              <div className="text-acc-17px font-acc-600 tracking-default text-grey-90">{topSectionKey}</div>
              <div className="mt-0.5 flex flex-row flex-wrap items-center">
                <span className="text-acc-13px font-acc-700 tracking-default text-grey-90">{"Fully met: "}</span>
                <span className="text-acc-13px font-acc-700 tracking-default text-green-light-text">
                  &nbsp;
                  {`${totalAchievedForSection} / ${learnObjectivesForTopSection.length} (${percentageForSection}%)`}
                </span>
                {totalPartialForSection !== undefined && (
                  <>
                    <span className="text-acc-13px font-acc-700 tracking-default text-grey-90">
                      &nbsp;{"- Partially met: "}
                    </span>
                    <span className="text-acc-13px font-acc-700 tracking-default text-orange-50">
                      &nbsp;
                      {`${totalPartialForSection} / ${learnObjectivesForTopSection.length} (${partialPercentageForSection}%)`}
                    </span>
                  </>
                )}
              </div>
            </div>
            {sections.map((unsortedSection) => {
              const section = unsortedSection.sort((a, b) => {
                if (props.sortLOsUsingDisplayName) {
                  return SPAPRPUtils.sortLOsUsingDisplayName(a, b);
                } else {
                  return SPAPRPUtils.sortLOsUsingName(a, b);
                }
              });

              return (
                <div key={section[0].Section}>
                  {section.map((learningOutcome, index) => {
                    const automaticCount = props.useAutoLOs
                      ? getAutomaticOccurrencesOfLearningOutcome(learningOutcome, props.evidence)
                      : getOccurrencesOfLearningOutcome(learningOutcome, props.evidence);
                    const manualCount = props.useAutoLOs
                      ? getManualOccurrencesOfLearningOutcome(learningOutcome, props.evidence)
                      : 0;
                    const numberRequired = learningOutcome.NumberRequired ?? 0;
                    const loAchieved = numberRequired > 0 && automaticCount >= numberRequired;

                    return (
                      <div key={learningOutcome.ID}>
                        {index === 0 && (
                          <>
                            {learningOutcome.Section ? (
                              <div className="border-y border-grey-30 bg-grey-00 px-4 py-1.5 text-acc-12px font-acc-600 text-grey-90">
                                {learningOutcome.Section}
                              </div>
                            ) : (
                              <div className="border-t border-grey-30" />
                            )}
                          </>
                        )}
                        {props.rowsAsButtons ? (
                          <div>
                            <IonItem
                              className={classNames("ion-item-no-padding h-full w-full bg-grey-00", {
                                "bg-white": index % 2 === 0,
                                "mb-4": index === section.length - 1,
                              })}
                              detail={false}
                              button={true}
                              onClick={() => props.learningOutcomeRowPressed(learningOutcome)}
                            >
                              <div className="flex w-full flex-col">
                                <div
                                  className={classNames("flex w-full flex-row items-center justify-between px-4 py-2")}
                                >
                                  <div
                                    className={classNames(
                                      "mr-4 w-full text-acc-12px font-acc-normal tracking-[-0.1] text-grey-90",
                                      {
                                        "text-acc-15px font-semibold": !!learningOutcome.SubTitle,
                                      }
                                    )}
                                  >
                                    {learningOutcome.Description}
                                  </div>
                                  <div className="flex flex-shrink-0 flex-row items-center">
                                    <div className="flex flex-shrink-0">
                                      <div className="mr-1.5 h-6 w-6">
                                        {loAchieved && (
                                          <img
                                            className="img-no-select h-6 w-6"
                                            src={Icon_Tick}
                                          />
                                        )}
                                      </div>
                                      <LearningOutcomeCount
                                        loAchieved={loAchieved}
                                        automaticCount={automaticCount}
                                        learningOutcome={learningOutcome}
                                        hideTotal={props.hideTotal}
                                        useRAGForTotal={props.useRAGForTotal}
                                      />
                                    </div>
                                    {props.useAutoLOs && (
                                      <div className="ml-2 flex w-11 flex-shrink-0 justify-center border-l border-grey-30">
                                        <div className="text-acc-13px font-acc-600 tracking-default text-grey-50">
                                          {manualCount}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {learningOutcome.SubTitle && (
                                  <div className="w-full px-4 pb-2">
                                    <div className="mr-4 w-full whitespace-pre-wrap text-acc-12px font-acc-normal leading-default tracking-[-0.1] text-grey-90">
                                      {learningOutcome.SubTitle}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </IonItem>
                          </div>
                        ) : (
                          <div
                            className={classNames(
                              "flex flex-row items-center justify-between border-b border-grey-30 bg-grey-00 px-4 py-1.5",
                              {
                                "bg-white": index % 2 === 0,
                                "mb-4": index === section.length - 1,
                              }
                            )}
                          >
                            <div className="mr-4 w-full text-acc-12px font-acc-normal tracking-[-0.1] text-grey-90">
                              {learningOutcome.Description}
                            </div>
                            <div className="flex flex-shrink-0 flex-row items-center">
                              <div className="flex flex-shrink-0">
                                <div className="mr-1.5 h-6 w-6">
                                  {loAchieved && (
                                    <img
                                      className="img-no-select h-6 w-6"
                                      src={Icon_Tick}
                                    />
                                  )}
                                </div>
                                <LearningOutcomeCount
                                  loAchieved={loAchieved}
                                  automaticCount={automaticCount}
                                  learningOutcome={learningOutcome}
                                  hideTotal={props.hideTotal}
                                />
                              </div>
                              {props.useAutoLOs && (
                                <div className="ml-2 flex w-11 flex-shrink-0 justify-center border-l border-grey-30">
                                  <div className="text-acc-13px font-acc-600 tracking-default text-grey-50">
                                    {manualCount}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

type LearningOutcomeCountProps = {
  loAchieved: boolean;
  automaticCount: number;
  learningOutcome: LearningObjective;
  hideTotal?: boolean;
  useRAGForTotal?: boolean;
};

const LearningOutcomeCount = (props: LearningOutcomeCountProps) => {
  if (props.hideTotal) {
    return (
      <div>
        <span
          className={classNames("text-acc-15px font-acc-700 tracking-default", {
            "text-grey-50": !props.loAchieved,
            "text-green-light-text": props.loAchieved,
            [`${getTextColor(props.automaticCount)}`]: props.useRAGForTotal,
          })}
        >
          {props.automaticCount ?? 0}
        </span>
      </div>
    );
  }

  return (
    <div>
      <span
        className={classNames("text-acc-15px font-acc-700 tracking-default", {
          "!text-green-light-text": props.loAchieved,
          "!text-orange-50":
            props.automaticCount > 0 && props.automaticCount < (props.learningOutcome.NumberRequired ?? 0),
          "!text-cta-red": props.automaticCount === 0,
        })}
      >
        {`${props.automaticCount} / ${props.learningOutcome.NumberRequired ?? 0}`}
      </span>
    </div>
  );
};

export default LearningOutcomesList;
